/**
 * localStorage I/O for user-authored levels and the in-progress draft.
 *
 * Storage layout (versioned so future schema changes can migrate):
 *   stickhook.levels.v1         → Record<slug, StoredLevel>
 *   stickhook.editor.draft.v1   → StoredLevel (autosaved)
 *
 * Everything funnels through this module so quota/parse failures are handled
 * in exactly one place.
 */

import type { LevelData } from '../gameplay/LevelLoader.js';
import { parseJson, validate, cloneLevel, type ValidateResult } from './LevelSchema.js';

const KEY_LEVELS = 'stickhook.levels.v1';
const KEY_DRAFT = 'stickhook.editor.draft.v1';

export interface StoredLevel {
  data: LevelData;
  meta: {
    slug: string;
    updatedAt: number;
    sourceId?: string;
  };
}

type LevelMap = Record<string, StoredLevel>;

// --- Levels -----------------------------------------------------------------

export function listUser(): StoredLevel[] {
  const map = readMap();
  return Object.values(map).sort((a, b) => b.meta.updatedAt - a.meta.updatedAt);
}

export function get(slug: string): StoredLevel | null {
  return readMap()[slug] ?? null;
}

export function save(slug: string, data: LevelData, sourceId?: string): StoredLevel {
  const map = readMap();
  const meta: StoredLevel['meta'] = { slug, updatedAt: Date.now() };
  if (sourceId !== undefined) meta.sourceId = sourceId;
  const stored: StoredLevel = { data: cloneLevel(data), meta };
  map[slug] = stored;
  writeMap(map);
  return stored;
}

export function remove(slug: string): void {
  const map = readMap();
  if (slug in map) {
    delete map[slug];
    writeMap(map);
  }
}

/** Deterministic, URL-safe slug derived from a free-form name. Disambiguated
 *  by appending `-2`, `-3`, … if the base slug is already taken. */
export function nextSlug(name: string): string {
  const base = (name || 'untitled')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 40) || 'untitled';
  const map = readMap();
  if (!(base in map)) return base;
  for (let i = 2; i < 1000; i++) {
    const cand = `${base}-${i}`;
    if (!(cand in map)) return cand;
  }
  return `${base}-${Date.now()}`;
}

// --- Draft ------------------------------------------------------------------

export function getDraft(): StoredLevel | null {
  const raw = safeRead(KEY_DRAFT);
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as StoredLevel;
    const v = validate(parsed?.data);
    if (!v.ok) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setDraft(data: LevelData, slug = '__draft__', sourceId?: string): void {
  const meta: StoredLevel['meta'] = { slug, updatedAt: Date.now() };
  if (sourceId !== undefined) meta.sourceId = sourceId;
  const stored: StoredLevel = { data: cloneLevel(data), meta };
  safeWrite(KEY_DRAFT, JSON.stringify(stored));
}

export function clearDraft(): void {
  safeRemove(KEY_DRAFT);
}

// --- Import / Export --------------------------------------------------------

export function exportJson(data: LevelData): string {
  return JSON.stringify(data, null, 2);
}

export function importJson(text: string): ValidateResult {
  return parseJson(text);
}

// --- Internals --------------------------------------------------------------

function readMap(): LevelMap {
  const raw = safeRead(KEY_LEVELS);
  if (raw === null) return {};
  try {
    const parsed = JSON.parse(raw) as LevelMap;
    if (!parsed || typeof parsed !== 'object') return {};
    // Drop entries whose data no longer matches the schema (defensive).
    const out: LevelMap = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (v && validate(v.data).ok) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function writeMap(map: LevelMap): void {
  safeWrite(KEY_LEVELS, JSON.stringify(map));
}

function safeRead(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // Quota or privacy mode — surface in console; callers can present UI.
    console.warn('[LevelStore] write failed', key, e);
    throw e;
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}
